import {
    useMutation,
    UseMutationOptions,
    useQuery,
    useQueryClient,
    UseQueryOptions,
} from "@tanstack/react-query";
import { createDownloadBlob, useAccessToken, useStandardErrorHandler } from "modules/common";
import { toast } from "react-toastify";
import { getDocumentContentFullpath } from "services/documents";
import {
    getEducationDocuments,
    createEducationDocument,
    GetEducationDocumentRequest,
    EducationDocumentPaginatedResponse,
    CreateEducationDocumentRequest,
    editEducationDocument,
    EditEducationDocumentRequest,
    BulkEditEducationDocumentRequest,
    deleteEducationDocument,
    EducationDocumentCategory,
    CreateEducationDocumentCategoryRequest,
    createEducationDocumentCategory,
    editEducationDocumentCategory,
    getEducationDocumentCategories,
    bulkEditEducationDocuments,
    EducationDocument as EducationDocumentEntry,
    deleteEducationDocumentCategory,
    bulkDeleteEducationDocuments,
} from "services/educationDocuments";

export const useGetEducationDocuments = (
    data: GetEducationDocumentRequest,
    options: UseQueryOptions<EducationDocumentPaginatedResponse> = {}
) => {
    const errorHandler = useStandardErrorHandler("Greška pri dohvaćanju edukacijskih dokumenata");
    const accessToken = useAccessToken();
    const { enabled, ...opts } = options;

    const queryFn = () => getEducationDocuments(data, accessToken!);

    return useQuery({
        queryKey: ["educationDocuments", data],
        queryFn,
        onError: errorHandler,
        keepPreviousData: true,
        ...opts,
        enabled: Boolean(data.categoryGuid),
    });
};

export const useGetEducationDocumentCategoriesAsItems = () => {
    const { data: recordCategoriesData } = useGetEducationDocumentCategories();

    return recordCategoriesData?.map(category => ({
        label: category.name,
        id: category.guid,
    }));
};

export const useCreateEducationDocument = (
    uploadedFile: File | null,
    options?: UseMutationOptions<EducationDocumentEntry, unknown, CreateEducationDocumentRequest>
) => {
    const accessToken = useAccessToken();
    const queryClient = useQueryClient();
    const errorHandler = useStandardErrorHandler("Greška pri stvaranju edukacijskog dokumenta");
    
    return useMutation({
        mutationFn: (data: CreateEducationDocumentRequest) =>
            createEducationDocument(data, uploadedFile, accessToken!),
        onError: errorHandler,
        onSuccess: () => {
            queryClient.invalidateQueries(["educationDocuments"]);
            queryClient.invalidateQueries(["educationDocumentCategories"]);
            toast.success("Dokument uspješno stvoren");
        },
        ...options,
    });
};

export const useEditEducationDocument = (uploadedFile: File | null) => {
    const accessToken = useAccessToken();
    const queryClient = useQueryClient();
    const errorHandler = useStandardErrorHandler("Greška pri uređivanju edukacijskog dokumenta");

    return useMutation({
        mutationFn: (data: EditEducationDocumentRequest) =>
            editEducationDocument(data, uploadedFile, accessToken!),
        onError: errorHandler,
        onSuccess: () => {
            queryClient.invalidateQueries(["educationDocuments"]);
            queryClient.invalidateQueries(["educationDocumentCategories"]);
            toast.success("Edukacijski dokument uspješno uređen.");
        },
    });
};

export const useBulkEditEducationDocument = () => {
    const accessToken = useAccessToken();
    const queryClient = useQueryClient();
    const errorHandler = useStandardErrorHandler(
        "Greška pri grupnom uređivanju edukacijskih dokumenata"
    );

    return useMutation({
        mutationFn: (data: BulkEditEducationDocumentRequest) =>
            bulkEditEducationDocuments(data, accessToken!),
        onError: errorHandler,
        onSuccess: () => {
            queryClient.invalidateQueries(["educationDocuments"]);
            queryClient.invalidateQueries(["educationDocumentCategories"]);
            toast.success("Edukacijski dokumenti su uspješno uređeni");
        },
    });
};

export const useDeleteEducationDocument = () => {
    const accessToken = useAccessToken();
    const queryClient = useQueryClient();
    const errorHandler = useStandardErrorHandler("Greška pri brisanju edukacijskog dokumenta");

    return useMutation({
        mutationFn: (guid: string) => deleteEducationDocument(guid, accessToken!),
        onError: errorHandler,
        onSuccess: () => {
            queryClient.invalidateQueries(["educationDocuments"]);
            toast.success("Edukacijski dokument je uspješno obrisan");
        },
    });
};

export const useBulkDeleteEducationDocuments = (
    options: UseMutationOptions<unknown, unknown, string[]> = {}
) => {
    const accessToken = useAccessToken();
    const queryClient = useQueryClient();
    const errorHandler = useStandardErrorHandler(
        "Greška pri grupnom brisanju edukacijskih dokumenata"
    );

    return useMutation({
        mutationFn: (guids: string[]) => bulkDeleteEducationDocuments(guids, accessToken!),
        onError: errorHandler,
        onSuccess: () => {
            queryClient.invalidateQueries(["educationDocuments"]);
            toast.success("Edukacijski dokumenti su uspješno obrisani");
        },
        ...options,
    });
};

export const useGetEducationDocumentCategories = (
    options: UseQueryOptions<EducationDocumentCategory[]> = {}
) => {
    const errorHandler = useStandardErrorHandler("Greška pri dohvaćanju kategorija evidencije");
    const accessToken = useAccessToken();
    const { enabled, ...opts } = options;

    const queryFn = () => getEducationDocumentCategories(accessToken!);

    return useQuery({
        queryKey: ["educationDocumentCategories"],
        queryFn,
        onError: errorHandler,
        keepPreviousData: true,
        ...opts,
    });
};

export const useCreateEducationDocumentCategory = (
    options?: UseMutationOptions<EducationDocumentCategory, unknown, EducationDocumentCategory>
) => {
    const accessToken = useAccessToken();
    const queryClient = useQueryClient();
    const errorHandler = useStandardErrorHandler(
        "Greška pri stvaranju kategorije edukacijskog dokumenta"
    );

    return useMutation({
        mutationFn: (data: EducationDocumentCategory) => {
            const createRequest: CreateEducationDocumentCategoryRequest = {
                name: data.name,
            };
            return createEducationDocumentCategory(createRequest, accessToken!);
        },
        onError: errorHandler,
        onSuccess: () => {
            queryClient.invalidateQueries(["educationDocumentCategories"]);
            toast.success("Kategorija edukacijskih dokumenata je uspješno stvorena");
        },
        ...options,
    });
};

export const useEditEducationDocumentCategory = (
    options?: UseMutationOptions<
        EducationDocumentCategory,
        unknown,
        CreateEducationDocumentCategoryRequest
    >
) => {
    const accessToken = useAccessToken();
    const queryClient = useQueryClient();
    const errorHandler = useStandardErrorHandler(
        "Greška pri uređivanju kategorije edukacijskog dokumenta"
    );

    return useMutation({
        mutationFn: (data: CreateEducationDocumentCategoryRequest) =>
            editEducationDocumentCategory(data, accessToken!),
        onError: errorHandler,
        onSuccess: () => {
            queryClient.invalidateQueries(["educationDocumentCategories"]);
            toast.success("Kategorija edukacijskih dokumenata je uspješno uređena");
        },
        ...options,
    });
};

export const useDeleteEducationDocumentCategory = (
    options?: UseMutationOptions<unknown, unknown, string>
) => {
    const accessToken = useAccessToken();
    const queryClient = useQueryClient();
    const errorHandler = useStandardErrorHandler(
        "Greška pri brisanju kategorije edukacijskog dokumenta"
    );

    return useMutation({
        mutationFn: (guid: string) => deleteEducationDocumentCategory(guid, accessToken!),
        onError: errorHandler,
        onSuccess: () => {
            queryClient.invalidateQueries(["educationDocumentCategories"]);
            toast.success("Kategorija edukacijskih dokumenata je uspješno obrisana");
        },
        ...options,
    });
};

export const downloadFile = async (fullPath: string, fileName: string) => {
    const response = await getDocumentContentFullpath(fullPath);
    createDownloadBlob(response, fileName);
};
